/**
 * Context and hook behind `TableControlsSlot.tsx` — kept apart from the components so that file
 * exports components only (fast refresh). The mechanics are described there.
 */

import { createContext, useContext, useEffect, useId } from 'react';

export interface TableControlsSlotContextValue {
  node: HTMLElement | null;
  register: (el: HTMLElement | null) => void;
  owner: string | null;
  claim: (id: string) => void;
  release: (id: string) => void;
}

export const TableControlsSlotContext = createContext<TableControlsSlotContextValue | null>(null);

/**
 * For the table: the slot's node while this table owns it, `null` otherwise (no provider, no slot
 * placed, another table got there first, or `enabled` is false — a computer).
 */
export function useTableControlsSlot(enabled: boolean): HTMLElement | null {
  const context = useContext(TableControlsSlotContext);
  const id = useId();
  const claim = context?.claim;
  const release = context?.release;

  useEffect(() => {
    if (!enabled || !claim || !release) return;
    claim(id);
    return () => release(id);
  }, [enabled, claim, release, id]);

  if (!enabled || !context || context.owner !== id) return null;
  return context.node;
}
